import {Component, ElementRef, Input, OnChanges, OnDestroy, ViewChild} from '@angular/core';
import {NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';

import {EvaluationsService} from '../services/evaluations.service';
import {LocalRecordingsService, RecordingsAccess} from '../services/local-recordings.service';

/**
 * The downloaded recording, played next to the criterion it settles.
 *
 * There are two ways to reach a three-hour file that lives on an external
 * drive, and the component tries both:
 *
 * 1. **The Express server**, `/local-video/{id}` — only where it can see the
 *    disks, which is the analyser's own machine. A `file://` link cannot be
 *    embedded or even followed from a web page (Chrome blocks it and says
 *    nothing), so the server hands the file out over HTTP instead, with Range
 *    support, which is what makes `seekTo()` worth having.
 * 2. **The disk itself**, through the File System Access API. On
 *    ballscore.app the route above is always a 404 — Cloud Run has no drives —
 *    and ballscore.app is where reviewing actually happens. So the admin points
 *    the browser at the folder once and the file is read locally and played
 *    from an object URL. Nothing is uploaded and the server never sees it; the
 *    permission model, which costs one click per browsing session, is in
 *    `LocalRecordingsService`.
 *
 * When neither works the component used to render nothing at all, which on
 * ballscore.app read as a missing feature rather than as a file that is
 * somewhere else. Given `localFile` it now says which of the two it is, and
 * offers the folder.
 */
@Component({
  selector: 'app-recording-player',
  standalone: true,
  imports: [NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, MatButtonModule, MatIconModule],
  template: `
    <div class="player" *ngIf="url">
      <video #video [src]="url" controls preload="metadata"
             (loadedmetadata)="applyStart()"></video>
      <button mat-button class="popout" [disabled]="busy" (click)="openInTab()">
        <mat-icon>open_in_new</mat-icon>
        Open in a new tab
      </button>
    </div>

    <div class="elsewhere" *ngIf="!url && localFile">
      <ng-container [ngSwitch]="access">
        <span *ngSwitchCase="'granted'">
          The connected folder does not hold this recording — the season is
          spread over two drives, so it may be on the other one.
        </span>
        <span *ngSwitchCase="'prompt'">
          The recording is on your disk. Chrome drops the folder permission when
          the browser restarts, so it takes one click to come back.
        </span>
        <span *ngSwitchCase="'unsupported'">
          The recording is not served from here — it is on the machine that
          analysed the game, and this browser has no directory picker to read it
          off the disk. Use <em>Recording on disk</em> to copy the path.
        </span>
        <span *ngSwitchDefault>
          The recording is not served from here — it is on the machine that
          analysed the game. Point the browser at the folder on that disk and it
          plays right here; nothing is uploaded.
        </span>
      </ng-container>

      <button mat-stroked-button class="connect" *ngIf="access !== 'unsupported'"
              [disabled]="busy" (click)="connect()">
        <mat-icon>folder_open</mat-icon>
        {{ connectLabel }}
      </button>
    </div>
  `,
  styles: `
    .player {
      padding: 8px 0;
    }

    video {
      width: 100%;
      max-width: 640px;
      border-radius: 4px;
      background: #000;
      display: block;
    }

    .elsewhere {
      padding: 4px 0 8px;
      font-size: 12px;
      color: rgba(0, 0, 0, 0.54);
      max-width: 640px;
    }

    .connect {
      display: block;
      margin-top: 8px;
    }

    .popout {
      margin-top: 4px;
    }
  `,
})
export class RecordingPlayerComponent implements OnChanges, OnDestroy {

  @Input() videoId: number;
  /** Where to start. Changing it seeks a player that is already loaded. */
  @Input() at: number | null = null;
  /**
   * The path the analyser recorded for this game. Only used to tell "there is
   * a recording, just not here" apart from "there is no recording" — without
   * it the component stays as invisible as it always was.
   */
  @Input() localFile: string | null = null;

  @ViewChild('video') video: ElementRef<HTMLVideoElement>;

  url: string | null = null;
  access: RecordingsAccess = 'none';
  busy = false;

  private loadedFor: number | null = null;
  /** Revoked by hand: an object URL pins the file until it is let go. */
  private objectUrl: string | null = null;

  constructor(private evaluations: EvaluationsService,
              private recordings: LocalRecordingsService) {
  }

  ngOnChanges(): void {
    if (this.videoId !== this.loadedFor) {
      this.loadedFor = this.videoId;
      this.load();
      return;
    }
    this.seekTo(this.at);
  }

  ngOnDestroy(): void {
    this.release();
  }

  get connectLabel(): string {
    switch (this.access) {
      case 'granted':
        return 'Add another folder';
      case 'prompt':
        return 'Reconnect the disk';
      default:
        return 'Connect the recordings folder';
    }
  }

  /**
   * Opens the picker, or re-asks for a folder already remembered. Both need a
   * click, which is why this is a button rather than a silent retry on load.
   */
  async connect(): Promise<void> {
    this.busy = true;
    try {
      this.access = this.access === 'prompt'
        ? await this.recordings.grant()
        : await this.recordings.connect();
      if (this.access === 'granted') {
        await this.load();
      }
    } finally {
      this.busy = false;
    }
  }

  /**
   * The same recording in a tab of its own, for scrubbing about without
   * losing the queue. A `file://` link cannot do this — Chrome refuses to
   * follow one from a web page — which is why copying the path was the only
   * way round it before.
   *
   * A file read off the disk gets its own object URL here, deliberately not
   * revoked: the one the player is using dies with the next game in the queue,
   * and it would take the new tab down with it mid-play.
   */
  async openInTab(): Promise<void> {
    if (!this.url) {
      return;
    }
    if (!this.objectUrl) {
      window.open(this.url, '_blank', 'noopener');
      return;
    }
    this.busy = true;
    try {
      const own = await this.recordings.open(this.videoId);
      window.open(own ?? this.url, '_blank', 'noopener');
    } finally {
      this.busy = false;
    }
  }

  /** An object URL carries no `#t=`, so the start is set once metadata is in. */
  applyStart(): void {
    if (this.at !== null && this.at !== undefined) {
      this.seekTo(this.at);
    }
  }

  seekTo(seconds: number | null): void {
    const player = this.video?.nativeElement;
    if (!player || seconds === null || seconds === undefined) {
      return;
    }
    player.currentTime = seconds;
    // A paused player shows the new frame only once it has seeked; playing is
    // what the reviewer wanted anyway when they clicked a timestamp.
    player.play().catch(() => undefined);
  }

  /**
   * The server first, because where it answers it needs neither permission nor
   * a click — that is the analyser's own machine, and its behaviour should not
   * change. Everywhere else the disk is the only source there is.
   */
  private async load(): Promise<void> {
    const wanted = this.videoId;
    this.release();
    this.url = null;
    try {
      const served = await this.evaluations.recordingUrl(wanted, this.at);
      if (wanted === this.loadedFor) {
        this.url = served;
      }
      return;
    } catch (e) {
      // Not served here; the disk is the remaining hope.
    }
    this.access = await this.recordings.access();
    const fromDisk = await this.recordings.open(wanted);
    // The queue moves on while a 4 GB file is being opened. Whatever came back
    // belongs to the game that has already gone, so let it go too.
    if (wanted !== this.loadedFor) {
      if (fromDisk) {
        URL.revokeObjectURL(fromDisk);
      }
      return;
    }
    this.objectUrl = fromDisk;
    this.url = fromDisk;
  }

  private release(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }
}
